import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LinkComponent } from '../../atoms/link/link.component';
import { AlertType } from '../../../shared/enums/alert-type.enums';

import { IconComponent } from '../../atoms/icon/icon.component';
import { Sizes, Colors } from '../../../shared/enums/shared.enums';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { Link } from '../../../shared/interfaces/link.interface';
import { HrefType } from '../../../shared/enums/href-type.enums';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  CaasDisruptor,
  CaasLink,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { CapitalizePipe } from '../../capitalize.pipe';
@Component({
  selector: 'hdi-alert',
  standalone: true,
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.scss',
  imports: [
    CommonModule,
    FontAwesomeModule,
    LinkComponent,
    IconComponent,
    CapitalizePipe,
  ],
  animations: [
    // Dropdown animation with transform
    trigger('dropdownAnimation', [
      state(
        'hidden',
        style({
          transform: 'scaleY(0)',
          opacity: 0,
          transformOrigin: 'top',
          visibility: 'hidden',
          display: 'none',
        })
      ),
      state(
        'visible',
        style({
          transform: 'scaleY(1)',
          opacity: 1,
          visibility: 'visible',
          display: 'block',
        })
      ),
      transition('hidden <=> visible', [animate('200ms ease-in-out')]),
    ]),

    // Fade animation with opacity only
    trigger('fadeAnimation', [
      state(
        'close',
        style({
          opacity: 0,
          display: 'none',
        })
      ),
      state(
        'open',
        style({
          opacity: 1,
          display: 'block',
        })
      ),
      transition('open <=> close', [animate('200ms ease-in-out')]),
    ]),
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class AlertComponent implements OnInit, OnChanges {
  @Input() type: AlertType = AlertType.NOTIFICATION;
  @Input() title: string = 'Wichtige Bekanntmachung';
  @Input() dropdownText?: string;
  @Input() link?: Link;
  @Input() isDropdownOpen: boolean = false;
  @Input() isAlertClosed: boolean = false;
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() headerIcon: string = 'triangle-exclamation';
  @Input() actionIcon: string = 'arrow-right-long';
  @Input() iconSize: Sizes = Sizes.MEDIUM;
  @Input() isExpandable: boolean = true;
  @Input() href: string = '';
  @Input() caasData?: CaasDisruptor;

  alertClass: string = '';
  isdropdownAnimationDone: boolean = false;
  fadeAnimationDone: boolean = false;
  alertType = AlertType;
  Sizes = Sizes;
  HrefType = HrefType;
  currentTime!: Date;
  startTime!: Date;
  endTime!: Date;
  isWithinTimeframe!: boolean;

  @Output() closed = new EventEmitter<void>();
  @ContentChild(LinkComponent)
  contentRef!: LinkComponent;

  get iconColor(): Colors {
    return this.type === AlertType.WARNING ||
      this.type === AlertType.NOTIFICATION
      ? Colors.BLACK
      : Colors.WHITE;
  }

  constructor(
    private caasBodytextService: CaasBodytextService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.setAlertClass();
    this.currentTime = new Date();
    this.startTime = new Date('2024-06-24T14:00:00');
    this.endTime = new Date('2025-06-24T15:00:00');
    this.checkTimeframe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes['type']) {
    //   this.setAlertClass();
    // }
    if (changes['caasData'] && this.caasData) {
      this.setCaasContent();
    }
  }

  setAlertClass(): void {
    this.alertClass = '';
    if (this.type === this.alertType.NOTIFICATION) {
      this.alertClass = 'notification';
    } else if (this.type === this.alertType.WARNING) {
      this.alertClass = 'warning';
    } else if (this.type === this.alertType.ERROR) {
      this.alertClass = 'error';
    } else if (this.type === this.alertType.SUCCESS) {
      this.alertClass = 'success';
    } else if (this.type === this.alertType.CRITICAL) {
      this.alertClass = 'critical';
    }
  }

  private toggleTimeout!: ReturnType<typeof setTimeout>;

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeAlert(): void {
    this.isAlertClosed = true;
    this.isDropdownOpen = false;
    this.closed.emit();
  }

  checkTimeframe(): void {
    if (
      this.currentTime >= this.startTime &&
      this.currentTime <= this.endTime
    ) {
      this.isWithinTimeframe = true;
    } else {
      this.isWithinTimeframe = false;
    }
  }

  handleLinkClick(event: Event, href: string): void {
    // Revert to original handleLinkClick method
    if (href.startsWith('#')) {
      event.preventDefault();
      const targetElement = this.document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  setCaasContent() {
    if (this.caasData?.formData?.st_color) {
      this.type = this.setCaasAlertType(
        this.caasData.formData.st_color.value.identifier
      );
    }
    if (this.caasData?.formData?.st_header?.value) {
      this.title = this.setCaasTitle(this.caasData.formData.st_header.value);
    }
    if (this.caasData?.formData?.st_bodytext?.value) {
      this.dropdownText = this.setCaasDropdownText(
        this.caasData.formData.st_bodytext.value
      );
    }
    if (this.caasData?.formData?.st_link?.value?.formData) {
      this.link = this.setCaasLink(this.caasData.formData.st_link);
    }
    if (this.caasData?.formData?.st_icon) {
      this.headerIcon =
        this.caasBodytextService.getIcon(this.caasData.formData.st_icon) ?? '';
    }
  }

  setCaasAlertType(identifier?: string): AlertType {
    switch (identifier) {
      case 'green':
        return AlertType.SUCCESS;
      case 'orange':
        return AlertType.WARNING;
      case 'red':
        return AlertType.ERROR;
      case 'turquoise':
        return AlertType.NOTIFICATION;
      case 'black':
        return AlertType.CRITICAL;
      default:
        return AlertType.NOTIFICATION;
    }
  }

  setCaasTitle(title: string): string {
    return title;
  }

  setCaasDropdownText(text: string): string {
    return this.caasBodytextService.extractTextEditorContent(text ?? '');
  }

  setCaasLink(link: CaasLink): Link {
    return {
      label: link?.value?.formData?.lt_link_label?.value ?? '',
      hrefType: this.caasBodytextService.getHrefType(link),
      href: this.caasBodytextService.foundHref(link),
      target: '_blank',
      title: link?.value?.formData?.lt_link_title?.value ?? '',
    };
  }
}
